import { delay } from './delay.js';
import { RECIPE_CATALOG } from './recipeCatalog.js';
import { relevanceOf, SUITABLE_RELEVANCE } from '../lib/matchRecipes.js';

// SWAP POINT: online recipe search (typed meal idea -> ranked candidate recipes).
// Today this ranks the canned catalog. Replace the body with a real request;
// keep the signature and the returned shape.
//
// This runs ONLY when the user's own library has nothing suitable, or when they
// press "Try another" past the end of their local matches.

// Selection rules for recipes the APP picks on the user's behalf. They do not
// apply to anything the user cooked or saved themselves — that was already
// their call, and silently skipping it would defeat reuse.
export const PREFERRED_MAX_INGREDIENTS = 15;
export const MAX_INGREDIENTS = 20;
export const TARGET_TOTAL_MINUTES = 30;

// How well a recipe fits "manageable", ignoring whether it is the right dish.
// 1 is ideal; it falls off as the recipe gets longer or wider.
function simplicityScore(recipe) {
  const count = recipe.ingredients.length;
  const overIngredients = Math.max(0, count - PREFERRED_MAX_INGREDIENTS);
  const overTime = Math.max(0, recipe.totalMinutes - TARGET_TOTAL_MINUTES);

  // Ingredients past the preferred count cost more than minutes past the
  // target, because a long shopping list is the thing that actually stops a
  // weeknight meal from happening.
  const penalty = overIngredients * 0.08 + (overTime / TARGET_TOTAL_MINUTES) * 0.12;
  return Math.max(0, 1 - penalty);
}

export async function searchOnlineRecipes(query, { excludeIds = [] } = {}) {
  await delay(900);

  const excluded = new Set(excludeIds);

  const scored = RECIPE_CATALOG.filter((recipe) => !excluded.has(recipe.id))
    // Hard ceiling: the app never hands the user a 24-ingredient project it
    // chose for them.
    .filter((recipe) => recipe.ingredients.length <= MAX_INGREDIENTS)
    .map((recipe) => ({
      recipe,
      relevance: relevanceOf(query, recipe),
      simplicity: simplicityScore(recipe),
      match: { kind: 'web', lastCookedAt: null, savedAt: null },
    }))
    .filter((candidate) => candidate.relevance >= SUITABLE_RELEVANCE);

  // Match the requested dish first; only sort on simplicity between recipes
  // that match the dish about equally well.
  const band = (value) => Math.round(value * 20) / 20;

  return scored.sort((a, b) => {
    if (band(b.relevance) !== band(a.relevance)) return band(b.relevance) - band(a.relevance);
    if (b.simplicity !== a.simplicity) return b.simplicity - a.simplicity;
    return a.recipe.ingredients.length - b.recipe.ingredients.length;
  });
}
